import PlainDate from '@Domain/PlainDate';
import isConductedOnProvided from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Creations/CreateInterviewConsentPage/FormData/constraint/isConductedOnProvided';
import { AbstractValidatedClass, Joi, ValidatedClass, constraint } from '@verclam/form-validator';

type ConsentFormArgs = {
	conductedOn: PlainDate | null;
	participantContactIds: readonly number[];
	participantStaffMemberIds: readonly number[];
};

/**
 * The details of the interview about to be recorded, filled in on the consent screen. `conductedOn` is
 * unconditionally required, so the class-wide `ignoreNullValue` option cannot be used (it would make a
 * `null` date read as valid, `guides/forms.md` §1.1) — a custom function validates it instead. The two
 * participant lists are optional arrays, capped like their server counterpart (`ArrayMaxSize(50)`).
 */
@ValidatedClass()
class ConsentFormData extends AbstractValidatedClass<ConsentFormArgs> {
	@constraint(isConductedOnProvided, () => "La date de l'entretien est obligatoire")
	public readonly conductedOn: PlainDate | null;

	@constraint(
		Joi.array().max(50),
		() => "Un entretien ne peut pas compter plus de 50 participants de l'entourage",
	)
	public readonly participantContactIds: readonly number[];

	@constraint(Joi.array().max(50), () => 'Un entretien ne peut pas compter plus de 50 membres du personnel')
	public readonly participantStaffMemberIds: readonly number[];

	constructor(data: ConsentFormArgs) {
		super(data);
		this.conductedOn = data.conductedOn;
		this.participantContactIds = data.participantContactIds;
		this.participantStaffMemberIds = data.participantStaffMemberIds;
		Object.freeze(this);
	}

	/** The date starts at today, never `null`. */
	static createEmpty(): ConsentFormData {
		return new ConsentFormData({
			conductedOn: PlainDate.today(),
			participantContactIds: [],
			participantStaffMemberIds: [],
		});
	}

	setConductedOn(conductedOn: PlainDate | null): this {
		return this.cloneWith({ conductedOn } as Partial<ConsentFormArgs>);
	}

	setParticipantContactIds(participantContactIds: readonly number[]): this {
		return this.cloneWith({ participantContactIds } as Partial<ConsentFormArgs>);
	}

	setParticipantStaffMemberIds(participantStaffMemberIds: readonly number[]): this {
		return this.cloneWith({ participantStaffMemberIds } as Partial<ConsentFormArgs>);
	}
}

export default ConsentFormData;
